import { useEffect, useReducer, useRef, type ReactNode } from 'react';

/** Duración del fundido entre lo que estaba en pantalla y lo nuevo. */
export const FADE_MS = 500;
/** Al entrar o salir de Logo o Negro el fundido es más lento: la pantalla va apareciendo poco a poco. */
export const SLOW_FADE_MS = 1200;

/** ¿El estado es Logo o Negro? (`screenId` empieza así; OBS le antepone su estilo). */
export const isLogoOrBlack = (id: string) => /(^|\|)(negro|logo:)/.test(id);

type Layer = { key: string; id: string; node: ReactNode; leaving: boolean; ms: number };

/**
 * Fundido cruzado para las salidas: cuando cambia `id` (otra diapositiva, logo, negro, otro fondo o
 * video), lo nuevo va apareciendo encima mientras lo anterior se desvanece, y lo anterior se quita
 * al terminar, sin parpadeo. También funciona cuando lo nuevo es transparente (Negro en OBS).
 * Mientras `id` no cambia, el contenido se actualiza en su lugar (un video sigue).
 */
export function Crossfade({ id, children, ms = FADE_MS, slowMs = SLOW_FADE_MS, className }: { id: string; children: ReactNode; ms?: number; slowMs?: number; className?: string }) {
  const layers = useRef<Layer[]>([]);
  const counter = useRef(0);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const last = layers.current.at(-1);
  if (!last || last.id !== id) {
    counter.current += 1;
    const dur = last && (isLogoOrBlack(id) || isLogoOrBlack(last.id)) ? slowMs : ms;
    // Sin capa anterior (al abrir la salida) no hay fundido.
    layers.current = [...layers.current.map((l) => (l.leaving ? l : { ...l, leaving: true, ms: dur })), { key: `${id}#${counter.current}`, id, node: children, leaving: false, ms: dur }];
  } else {
    last.node = children;
  }

  const current = layers.current.at(-1)!;
  const leaving = layers.current.some((l) => l.leaving);
  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => {
      layers.current = layers.current.filter((l) => !l.leaving);
      rerender();
    }, current.ms);
    return () => clearTimeout(t);
  }, [id, leaving, current.ms]);

  const first = layers.current.length === 1 && counter.current === 1;
  return (
    <div className={`xfade${className ? ` ${className}` : ''}`}>
      {layers.current.map((l) => (
        <div key={l.key} className={`xfade__layer${l.leaving ? ' xfade__layer--old' : first ? '' : ' xfade__layer--in'}`} style={{ animationDuration: `${l.ms}ms` }} aria-hidden={l.leaving || undefined}>
          {l.node}
        </div>
      ))}
    </div>
  );
}
